import { requestStardogVoiceboxGenesis } from 'vet-bones/utils/voicebox';

import { limitConcurrentRequests } from 'src/constants/limits';
import { Suggestion } from 'src/containers/ProjectMapping/types';
import { Connection } from 'src/store/connections/types';
import { ProjectResource } from 'src/store/state/projects';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';
import { getClassIrisForSuggestionsRequest } from 'src/utils/suggestions/getClassIrisForSuggestionsRequest';
import { buildGenerateMappingsParameters } from 'src/utils/voicebox/hooks/parameters';
import {
  GenerateMappingsRequest,
  GenerateMappingsResponse,
} from 'src/utils/voicebox/hooks/types';
import { transformGenesisMappingsToSuggestions } from 'src/utils/voicebox/transformGenesisMappingsToSuggestions';

/**
 * Requests suggestions for resources using the voicebox service
 */

export const requestVoiceboxSuggestions =
  ({
    classIris,
    connection,
    projectId,
    resourceIds,
  }: {
    classIris: string[];
    connection: Connection;
    projectId: string;
    resourceIds: string[];
  }) =>
  async (_: AppDispatch, getState: ReduxStateGetter) => {
    const state = getState();
    const project = state.projects.projectsById[projectId];

    if (!project) {
      console.warn('!project');
      return null;
    }

    const classIrisForSuggestionsRequest = getClassIrisForSuggestionsRequest(
      classIris,
      project.diagram.model
    );

    const parameters = buildGenerateMappingsParameters({
      project,
      projectId,
      resourceIds: new Set(resourceIds),
      classIris: classIrisForSuggestionsRequest,
    });

    const resourceById: Record<string, ProjectResource> = {};
    resourceIds.forEach((resourceId) => {
      const resource =
        project.fileUploadsById[resourceId] ||
        project.virtualGraphsById[resourceId];
      if (resource) {
        resourceById[resourceId] = resource;
      }
    });

    const suggestions: Suggestion[] = [];
    await limitConcurrentRequests(async () => {
      const response = await requestStardogVoiceboxGenesis<
        GenerateMappingsRequest,
        GenerateMappingsResponse
      >({
        connectionIdx: connection.index,
        body: {
          endpoint: connection.endpoint,
          action: 'generate_mappings',
          parameters,
        },
      });

      if (response.results) {
        const fieldLabelToValueMapByResource: Record<
          string,
          Record<string, string>
        > = {};

        // Build field mapping for resources without headers
        Object.values(resourceById).forEach((resource) => {
          if (resource.fields && resource.options?.header === false) {
            const fieldMap: Record<string, string> = {};
            resource.fields.forEach((field) => {
              const { label } = field;
              if (label) {
                fieldMap[label] = field.value;
              }
            });
            fieldLabelToValueMapByResource[resource.name] = fieldMap;
          }
        });

        const suggestionsWithoutResource =
          transformGenesisMappingsToSuggestions(
            response.results,
            fieldLabelToValueMapByResource,
            resourceById
          );

        const singleResource =
          resourceIds.length === 1
            ? project.fileUploadsById[resourceIds[0]] ||
              project.virtualGraphsById[resourceIds[0]]
            : null;

        suggestionsWithoutResource.forEach((suggestion) => {
          let resource: ProjectResource | undefined;

          if (singleResource) {
            resource = singleResource;
          } else if (suggestion.source) {
            resource = resourceById[suggestion.source];
          }

          if (resource) {
            suggestions.push({ ...suggestion, resource });
          } else {
            console.warn(`couldn't match a suggestion to a resource`, {
              suggestion,
              resourceById,
            });
          }
        });
      }
    });

    return suggestions;
  };
